/**
 * Safety and progression rule engine for apnea training.
 *
 * This is where the training sources' constraints become enforceable. A plain
 * interval timer is easy; the value of this feature is that it refuses unsafe or
 * counterproductive sessions and explains why.
 *
 * Design decisions:
 *  - Rules return structured verdicts, never throw. A refusal is a UI state.
 *  - Severity distinguishes a hard `block` from a `warn` the user may proceed
 *    through. Nothing silently downgrades a block.
 *  - Every verdict carries a citation, so the user can see which source a
 *    restriction came from (NAUI vs. the supplementary program).
 *  - "Now" is always injected, never read from the clock inside these functions,
 *    so the rules are deterministic and testable.
 */

import { ApneaTableType } from '../data/nauiTables';
import { POST_EVENT_RULES } from '../data/blackoutSigns';
import { MaxStatic, evaluatePhase } from './apneaTables';

export type RuleSeverity = 'block' | 'warn' | 'info';

export interface RuleVerdict {
  ruleId: string;
  severity: RuleSeverity;
  message: string;
  citation: string;
  /** When a restriction lifts, for time-based blocks. */
  clearsAt?: string;
}

export type ApneaSessionType =
  | 'co2'
  | 'o2'
  | 'noContraction'
  | 'apneaWalk'
  | 'dryStatic'
  | 'bolt'
  | 'breathing';

/** Session types that count toward the weekly table-work limit. */
export const TABLE_SESSION_TYPES: ApneaSessionType[] = ['co2', 'o2', 'noContraction'];

/** Session types involving a genuine breath-hold, for post-event blocks. */
export const BREATH_HOLD_TYPES: ApneaSessionType[] = [
  'co2', 'o2', 'noContraction', 'apneaWalk', 'dryStatic', 'bolt',
];

export interface SessionSummary {
  sessionId: string;
  type: ApneaSessionType;
  /** ISO timestamp. */
  startedAt: string;
  /** 1-5; a falling trend blocks progression. */
  recovery?: number;
  warningSigns?: { occurred: boolean; kind?: 'lmc' | 'blackout' | 'other' };
  aborted?: boolean;
}

export interface BoltReading {
  /** ISO date (or timestamp; only the date part is used for day comparisons). */
  recordedAt: string;
  seconds: number;
}

export interface DiveSummary {
  /** ISO timestamps. */
  startTime: string;
  endTime?: string;
}

export interface RuleContext {
  now: Date;
  sessions: SessionSummary[];
  boltReadings: BoltReading[];
  dives: DiveSummary[];
  maxStatic?: MaxStatic;
  /** Whether a CO2 base has been completed, gating O2 work. */
  co2BaseComplete?: boolean;
  /** Upcoming planned dives, so we can block training the same day before one. */
  upcomingDives?: DiveSummary[];
}

// ---------------------------------------------------------------------------
// Constants, with their sources
// ---------------------------------------------------------------------------

/** No breath-hold training within this window after surfacing from a scuba dive. */
export const POST_DIVE_BLOCK_HOURS = 24;

/** Table sessions per rolling 7 days: warn at this many, block above it. */
export const WEEKLY_TABLE_WARN_AT = 3;
export const WEEKLY_TABLE_BLOCK_ABOVE = 3;

/** Consecutive declining BOLT readings that trigger a back-off. */
export const BOLT_DECLINE_DAYS = 3;

/** Minimum interval between max static attempts. */
export const MAX_TEST_INTERVAL_DAYS = 14;

/** Recovery score at or below this counts as poor. */
export const POOR_RECOVERY_THRESHOLD = 2;

const HOUR_MS = 3600_000;
const DAY_MS = 86_400_000;

const CITE_NAUI = 'NAUI Freediver Manual (2021)';
const CITE_PROGRAM = 'Freediver Breathing Program';

const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth()
  && a.getDate() === b.getDate();

const parseDate = (iso: string): Date | null => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

// ---------------------------------------------------------------------------
// Individual rules
// ---------------------------------------------------------------------------

/**
 * Rule 2 — after any LMC/blackout: NAUI stops training for the rest of the day;
 * the supplementary program pauses table work for a week. Both are applied, and
 * reported separately so the user can see which is which.
 */
export const checkPostEvent = (
  type: ApneaSessionType,
  ctx: RuleContext
): RuleVerdict[] => {
  const events = ctx.sessions.filter(s => s.warningSigns?.occurred
    && (s.warningSigns.kind === 'lmc' || s.warningSigns.kind === 'blackout'));
  if (events.length === 0) return [];

  const verdicts: RuleVerdict[] = [];
  const latest = events
    .map(e => parseDate(e.startedAt))
    .filter((d): d is Date => d !== null)
    .sort((a, b) => b.getTime() - a.getTime())[0];
  if (!latest) return [];

  if (BREATH_HOLD_TYPES.includes(type) && isSameDay(latest, ctx.now)) {
    verdicts.push({
      ruleId: 'post-event-same-day',
      severity: 'block',
      message:
        'You logged a loss of motor control or blackout today. No further breath-hold '
        + 'training today — your oxygen stores are depleted and you are more susceptible '
        + 'to another event. A night\'s rest is enough to recover.',
      citation: `${CITE_NAUI} p.28`,
    });
  }

  const pauseMs = POST_EVENT_RULES.conservativeTablePause.days * DAY_MS;
  const elapsed = ctx.now.getTime() - latest.getTime();
  if (TABLE_SESSION_TYPES.includes(type) && elapsed < pauseMs) {
    verdicts.push({
      ruleId: 'post-event-table-pause',
      severity: 'block',
      message:
        `Table training is paused for ${POST_EVENT_RULES.conservativeTablePause.days} days `
        + 'after a loss of motor control or blackout. Resume at a clearly easier level.',
      citation: `${CITE_PROGRAM}, Part I`,
      clearsAt: new Date(latest.getTime() + pauseMs).toISOString(),
    });
  }

  return verdicts;
};

/**
 * Rule 8 — no breath-hold training within 24 h after a dive, and none earlier the
 * same day as a planned dive. Residual inert gas plus breath-holding has no upside.
 */
export const checkDiveProximity = (
  type: ApneaSessionType,
  ctx: RuleContext
): RuleVerdict[] => {
  if (!BREATH_HOLD_TYPES.includes(type)) return [];
  const verdicts: RuleVerdict[] = [];

  const surfacings = ctx.dives
    .map(d => parseDate(d.endTime ?? d.startTime))
    .filter((d): d is Date => d !== null)
    .filter(d => d.getTime() <= ctx.now.getTime());

  if (surfacings.length > 0) {
    const latest = new Date(Math.max(...surfacings.map(d => d.getTime())));
    const hoursSince = (ctx.now.getTime() - latest.getTime()) / HOUR_MS;
    if (hoursSince < POST_DIVE_BLOCK_HOURS) {
      const remaining = Math.ceil(POST_DIVE_BLOCK_HOURS - hoursSince);
      verdicts.push({
        ruleId: 'post-dive-block',
        severity: 'block',
        message:
          `You dived within the last ${POST_DIVE_BLOCK_HOURS} hours. No breath-hold `
          + `training for another ${remaining} hour${remaining === 1 ? '' : 's'} — residual `
          + 'inert gas plus breath-holding carries real risk and no benefit.',
        citation: `${CITE_PROGRAM}, Part I`,
        clearsAt: new Date(latest.getTime() + POST_DIVE_BLOCK_HOURS * HOUR_MS).toISOString(),
      });
    }
  }

  const divesToday = (ctx.upcomingDives ?? [])
    .map(d => parseDate(d.startTime))
    .filter((d): d is Date => d !== null)
    .filter(d => d.getTime() > ctx.now.getTime() && isSameDay(d, ctx.now));

  if (divesToday.length > 0) {
    verdicts.push({
      ruleId: 'pre-dive-block',
      severity: 'block',
      message:
        'You have a dive planned today. Do not train breath-holding beforehand.',
      citation: `${CITE_PROGRAM}, Part I`,
    });
  }

  return verdicts;
};

/** Rule 5 — never both table types on the same day. */
export const checkSameDayTableConflict = (
  type: ApneaSessionType,
  ctx: RuleContext
): RuleVerdict[] => {
  if (type !== 'co2' && type !== 'o2') return [];
  const other: ApneaTableType = type === 'co2' ? 'o2' : 'co2';

  const conflict = ctx.sessions.some(s => {
    if (s.type !== other) return false;
    const when = parseDate(s.startedAt);
    return when !== null && isSameDay(when, ctx.now);
  });

  if (!conflict) return [];
  return [{
    ruleId: 'same-day-table-conflict',
    severity: 'block',
    message:
      `You already did a ${other.toUpperCase()} table today. Never run both table types `
      + 'on the same day — the two stimuli work against each other.',
    citation: `${CITE_PROGRAM}, Part IV`,
  }];
};

/** Rule 6 — 2-3 table sessions per rolling week. Warn at 3, block above. */
export const checkWeeklyTableVolume = (
  type: ApneaSessionType,
  ctx: RuleContext
): RuleVerdict[] => {
  if (!TABLE_SESSION_TYPES.includes(type)) return [];

  const weekAgo = ctx.now.getTime() - 7 * DAY_MS;
  const count = ctx.sessions.filter(s => {
    if (!TABLE_SESSION_TYPES.includes(s.type)) return false;
    const when = parseDate(s.startedAt);
    return when !== null && when.getTime() >= weekAgo && when.getTime() <= ctx.now.getTime();
  }).length;

  if (count >= WEEKLY_TABLE_BLOCK_ABOVE + 1) {
    return [{
      ruleId: 'weekly-table-volume-block',
      severity: 'block',
      message:
        `You have done ${count} table sessions in the last 7 days. More than `
        + `${WEEKLY_TABLE_BLOCK_ABOVE} is counterproductive — tables feel easy but are `
        + 'metabolically expensive, and overtraining shows up as a falling BOLT.',
      citation: `${CITE_PROGRAM}, Part IV`,
    }];
  }

  if (count >= WEEKLY_TABLE_WARN_AT) {
    return [{
      ruleId: 'weekly-table-volume-warn',
      severity: 'warn',
      message:
        `This would be table session ${count + 1} in 7 days. The guidance is 2-3 per week `
        + 'at most, and some instructors say 1-2.',
      citation: `${CITE_PROGRAM}, Part IV`,
    }];
  }

  return [];
};

/** Rule 4 — phase entry gated on measured max static. */
export const checkPhaseEligibility = (
  type: ApneaSessionType,
  phase: number | undefined,
  ctx: RuleContext
): RuleVerdict[] => {
  if (type !== 'co2' && type !== 'o2') return [];
  if (phase === undefined) return [];

  if (!ctx.maxStatic) {
    return [{
      ruleId: 'phase-needs-max-static',
      severity: 'block',
      message:
        'Record a BOLT reading or a max static attempt first — table holds are scaled '
        + 'from your measured maximum.',
      citation: `${CITE_PROGRAM}, Part IV`,
    }];
  }

  const verdict = evaluatePhase(phase, type, ctx.maxStatic);
  if (!verdict.eligible) {
    return [{
      ruleId: 'phase-above-ceiling',
      severity: 'block',
      message: verdict.reason ?? `Phase ${phase} is above your current ceiling.`,
      citation: `${CITE_PROGRAM}, Part IV`,
    }];
  }

  if (ctx.maxStatic.basis === 'estimated') {
    return [{
      ruleId: 'phase-on-estimate',
      severity: 'warn',
      message:
        'Your max static is estimated from BOLT, not measured. Stay at phase 1 until you '
        + 'have done a supervised max attempt.',
      citation: `${CITE_PROGRAM}, §2`,
    }];
  }

  return [];
};

/** Rule 9 — O2 tables gated behind a completed CO2 base. */
export const checkO2Gate = (
  type: ApneaSessionType,
  ctx: RuleContext
): RuleVerdict[] => {
  if (type !== 'o2') return [];
  if (ctx.co2BaseComplete) return [];
  return [{
    ruleId: 'o2-needs-co2-base',
    severity: 'block',
    message:
      'O2 tables come after a CO2 base. CO2 work is uncomfortable but self-limiting; '
      + 'O2 work feels comfortable while bringing you closer to blackout.',
    citation: `${CITE_PROGRAM}, §6`,
  }];
};

/** Rule 10 — three consecutive declining BOLT readings means back off. */
export const checkBoltTrend = (ctx: RuleContext): RuleVerdict[] => {
  const readings = ctx.boltReadings
    .map(r => ({ ...r, date: parseDate(r.recordedAt) }))
    .filter((r): r is BoltReading & { date: Date } => r.date !== null)
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  if (readings.length < BOLT_DECLINE_DAYS + 1) return [];

  const recent = readings.slice(-(BOLT_DECLINE_DAYS + 1));
  const declining = recent.every((r, i) => i === 0 || r.seconds < recent[i - 1].seconds);

  if (!declining) return [];
  return [{
    ruleId: 'bolt-declining',
    severity: 'warn',
    message:
      `Your BOLT has fallen ${BOLT_DECLINE_DAYS} days running — the earliest overtraining `
      + 'signal you get. Take two easy days before your next hard session.',
    citation: `${CITE_PROGRAM}, Part I`,
  }];
};

/** Rule 11 — falling recovery scores block progression (not the session itself). */
export const checkRecoveryTrend = (ctx: RuleContext): RuleVerdict[] => {
  const scored = ctx.sessions
    .filter(s => typeof s.recovery === 'number')
    .map(s => ({ ...s, date: parseDate(s.startedAt) }))
    .filter((s): s is SessionSummary & { date: Date } => s.date !== null)
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  if (scored.length < 2) return [];
  const recent = scored.slice(-3);
  const poor = recent.filter(s => (s.recovery ?? 5) <= POOR_RECOVERY_THRESHOLD);

  if (poor.length >= 2) {
    return [{
      ruleId: 'recovery-blocks-progression',
      severity: 'warn',
      message:
        'Recovery has scored poorly in your recent sessions. Hold at the current level or '
        + 'reduce it — a long hold with poor recovery is not a good repetition.',
      citation: `${CITE_PROGRAM}, Ch.3 §3.10`,
    }];
  }
  return [];
};

/**
 * Max static attempts: no closer together than 14 days. Max efforts carry most of
 * the risk and produce little of the adaptation.
 */
export const checkMaxTestEligibility = (ctx: RuleContext): RuleVerdict[] => {
  const attempts = ctx.sessions
    .filter(s => s.type === 'dryStatic')
    .map(s => parseDate(s.startedAt))
    .filter((d): d is Date => d !== null)
    .sort((a, b) => b.getTime() - a.getTime());

  if (attempts.length === 0) return [];

  const daysSince = (ctx.now.getTime() - attempts[0].getTime()) / DAY_MS;
  if (daysSince < MAX_TEST_INTERVAL_DAYS) {
    const remaining = Math.ceil(MAX_TEST_INTERVAL_DAYS - daysSince);
    return [{
      ruleId: 'max-test-too-soon',
      severity: 'block',
      message:
        `Your last max attempt was ${Math.floor(daysSince)} days ago. Wait `
        + `${remaining} more day${remaining === 1 ? '' : 's'} — one attempt per two weeks `
        + 'at most, since max efforts carry most of the risk and little of the gain.',
      citation: `${CITE_PROGRAM}, Part I`,
      clearsAt: new Date(attempts[0].getTime() + MAX_TEST_INTERVAL_DAYS * DAY_MS).toISOString(),
    }];
  }
  return [];
};

/** Whether a max static re-test is due, for prompting on the Today tab. */
export const isMaxTestDue = (ctx: RuleContext): boolean => {
  const attempts = ctx.sessions
    .filter(s => s.type === 'dryStatic')
    .map(s => parseDate(s.startedAt))
    .filter((d): d is Date => d !== null);

  if (attempts.length === 0) return true;
  const latest = Math.max(...attempts.map(d => d.getTime()));
  return (ctx.now.getTime() - latest) / DAY_MS >= MAX_TEST_INTERVAL_DAYS;
};

// ---------------------------------------------------------------------------
// Aggregate
// ---------------------------------------------------------------------------

export interface SessionRequest {
  type: ApneaSessionType;
  phase?: number;
}

export interface SessionDecision {
  allowed: boolean;
  blocks: RuleVerdict[];
  warnings: RuleVerdict[];
  /** Everything, in severity order, for display. */
  verdicts: RuleVerdict[];
}

/**
 * The single entry point the UI should use. Evaluates every applicable rule and
 * returns a decision; a session is allowed only when nothing blocks it.
 */
export const evaluateSession = (
  request: SessionRequest,
  ctx: RuleContext
): SessionDecision => {
  const { type, phase } = request;

  const verdicts: RuleVerdict[] = [
    ...checkPostEvent(type, ctx),
    ...checkDiveProximity(type, ctx),
    ...checkSameDayTableConflict(type, ctx),
    ...checkWeeklyTableVolume(type, ctx),
    ...checkO2Gate(type, ctx),
    ...checkPhaseEligibility(type, phase, ctx),
    ...(type === 'dryStatic' ? checkMaxTestEligibility(ctx) : []),
    ...checkBoltTrend(ctx),
    ...checkRecoveryTrend(ctx),
  ];

  const blocks = verdicts.filter(v => v.severity === 'block');
  const warnings = verdicts.filter(v => v.severity === 'warn');

  return {
    allowed: blocks.length === 0,
    blocks,
    warnings,
    verdicts: [
      ...blocks,
      ...warnings,
      ...verdicts.filter(v => v.severity === 'info'),
    ],
  };
};
